import { actions as usersActions } from '@/api/users';
import { Button, Card, Modal, useToastContext } from '@payaca/ambrosia';

import UntitledIcon from '@payaca/untitled-icons';
import React, {
  FC,
  PropsWithChildren,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';

type Props = {
  disableConnection?: boolean;
  disableConnectionMessage?: JSX.Element;
  allowConnectDisconnect: boolean;
  logo?: string;
  connectionName: string;
  disconnectionError?: string;
  hasConnectionExpired?: boolean;
  isConnected: boolean | undefined;
  onAddConnection: () => void;
  onRemoveConnection:
    | (() => Promise<unknown>)
    | ((onConnectionRemoved: (err?: unknown) => void) => void);
  disconnectionWarningMessage?: React.ReactNode;
};
const Connection: FC<PropsWithChildren<Props>> = ({
  disableConnection = false,
  disableConnectionMessage,
  allowConnectDisconnect,
  logo,
  children,
  connectionName,
  disconnectionError,
  hasConnectionExpired = false,
  isConnected,
  onAddConnection,
  onRemoveConnection,
  disconnectionWarningMessage,
}: PropsWithChildren<Props>): JSX.Element => {
  const dispatch = useDispatch();
  const { pushSuccess } = useToastContext();

  const [removingConnection, setRemovingConnection] = useState(false);
  const [hasDisconnectionError, setHasDisconnectionError] = useState(false);
  const [showRemoveConnectionModal, setShowRemoveConnectionModal] =
    useState<boolean>(false);

  const connectButtonComponent = useMemo(() => {
    return (
      <div>
        {hasConnectionExpired && (
          <UntitledIcon
            name="alert-triangle.3"
            className="mr-2 h-6 w-6 text-red-600"
          />
        )}
        <Button
          onClick={onAddConnection}
          variant="secondary"
          disabled={disableConnection}
        >
          {logo && <img src={logo} className="mr-2 size-6" />}
          Connect to {connectionName}
        </Button>
      </div>
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'connectionName', 'logo', 'onAddConnection', and 'disableConnection'
  }, [hasConnectionExpired]);

  const disconnectionErrorMessage = useMemo(() => {
    return disconnectionError
      ? disconnectionError
      : `Sorry, there was an error disconnecting from ${connectionName}. Please try again.`;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'connectionName'
  }, [disconnectionError]);

  const onConnectionRemoved = (err?: unknown) => {
    if (err) {
      setHasDisconnectionError(true);
    } else {
      setHasDisconnectionError(false);
      setShowRemoveConnectionModal(false);
      pushSuccess(`Successfully disconnected from ${connectionName}`);
    }
    dispatch(usersActions.getProfile());
    setRemovingConnection(false);
  };

  const removeConnection = async () => {
    setRemovingConnection(true);
    setHasDisconnectionError(false);

    // onRemoveConnection can be used in either callback style
    // or promise style, so we handle both cases here.
    const response = onRemoveConnection(onConnectionRemoved);
    if (response instanceof Promise) {
      try {
        await response;
        onConnectionRemoved();
      } catch (err) {
        onConnectionRemoved(err);
      }
    }
  };

  const disconnectButtonComponent = useMemo(() => {
    return (
      <div>
        <Button
          onClick={() => {
            setHasDisconnectionError(false);
            setShowRemoveConnectionModal(true);
          }}
          isProcessing={removingConnection}
          variant="secondary"
          className="disconnect"
        >
          <UntitledIcon name="check" className="mr-2 h-5 w-5 text-blue-600" />
          Disconnect from {connectionName}
        </Button>
      </div>
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: removeConnection
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: hasDisconnectionError
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: dispatch
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: disconnectionErrorMessage
  }, [
    connectionName,
    disconnectionErrorMessage,
    dispatch,
    hasDisconnectionError,
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React hook useMemo depends on `removeConnection`, which changes every render
    removeConnection,
    removingConnection,
  ]);

  return (
    <Card sizeVariant="lg">
      <Card.Body className="flex h-full flex-col">
        {/* title and icon */}
        <div className="flex pb-5">
          {logo && <img src={logo} className="mr-5 size-12" />}

          <h2>{connectionName}</h2>
        </div>

        {/* content */}
        <div className="grow">{children}</div>
        {disableConnection && disableConnectionMessage}
        {/* connect button */}
        {allowConnectDisconnect && (
          <div className="pt-4">
            {/* Connect/disconenct button */}
            {!isConnected ? connectButtonComponent : disconnectButtonComponent}
          </div>
        )}
        <Modal
          isOpen={showRemoveConnectionModal}
          onClose={() => setShowRemoveConnectionModal(false)}
          title={`Disconnect from ${connectionName}`}
          className="disconnect-warning-modal"
        >
          <Modal.Body>
            {disconnectionWarningMessage && (
              <p>{disconnectionWarningMessage}</p>
            )}
            <p>Are you sure you want to disconnect from {connectionName}?</p>
            {hasDisconnectionError && (
              <ConnectionContolAlert>
                {disconnectionErrorMessage}
              </ConnectionContolAlert>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button
                variant="secondary"
                onClick={() => setShowRemoveConnectionModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={removeConnection}
                isProcessing={removingConnection}
              >
                Yes, disconnect
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export const ConnectionContolAlert: FC<PropsWithChildren> = ({
  children,
}): JSX.Element => {
  return (
    <div className="flex flex-row px-3 pt-5 text-red-600">
      <UntitledIcon name="alert-triangle.3" className="mr-4 mt-2 h-5 w-5" />
      <div>{children}</div>
    </div>
  );
};

export default Connection;
